/**
 * Rainbow Mulch — Playground Project Builder
 * CONFETTI-PIECE SCATTER RENDERER (D-046, replaces D-035)
 *
 * ============================================================================
 *  WHAT THIS IS
 * ============================================================================
 * Renders the Step 3 blend preview as a scattered pile of real, photographed
 * mulch-piece cutouts — one to four colours, layered and rotated at random —
 * instead of D-035's flat tiled bands. The placement algorithm (sampling,
 * layering, sizing) is PORTED from the legacy page at
 * /pages/build-your-confetti-mix (sections/confetti-builder.liquid), which has
 * used this exact effect in production. Ported deliberately, not reinvented:
 * that page has already proven the visual works.
 *
 * WHAT WAS NOT PORTED, ON PURPOSE:
 *   - The legacy page's OWN colour-picker UI and its `alert()` on hitting the
 *     4-colour limit. That selection logic belongs to pb-builder.js's
 *     onColorToggle(), which already enforces the limit correctly via
 *     announce() (an accessible live region, not a blocking browser dialog).
 *     This module has no opinion on limits — it renders whatever colour list
 *     it is given. There is no alert() anywhere in this file, and no reason
 *     for one: it has no selection state to police.
 *   - Inline Liquid-templated URLs. The legacy page resolves piece URLs with
 *     `{{ 'x.png' | file_url }}` at template-render time. This module reads
 *     them as plain data from PBConfig.color.catalog[].confettiPieces (D-046),
 *     the same way D-034 already stores swatch URLs — ported into config, not
 *     copied as inline Liquid.
 *
 * ============================================================================
 *  PERFORMANCE — measured, not assumed (see D-046 for the full numbers)
 * ============================================================================
 * The source piece PNGs are ~1.55MB each at their native 1500×1500 resolution
 * — real, uncompressed product photography, never optimised for web. Loading
 * a full 4-colour blend at that size would cost ~6.2MB. Requesting
 * `?width=300` from Shopify's CDN transform (confirmed to work on Files, not
 * just product images) cuts that to ~98KB/file as PNG — and because every
 * modern browser sends `Accept: image/webp` on an <img> fetch, the CDN
 * actually serves ~21KB WebP with the alpha channel intact, for free, with no
 * format param needed (an explicit `&format=webp` was tested and is IGNORED —
 * only Accept-header negotiation works). Measured real cost: a 4-colour blend
 * (16 images) is ~352KB and loads in ~1.4s over 6 concurrent connections; the
 * full 11-colour, 44-image palette is under 1MB.
 *
 * So THIS FILE NEVER REQUESTS A SIZE — it appends `width=300` to every URL
 * itself (see PIECE_WIDTH below) rather than baking that choice into config,
 * because it is a rendering decision, not settled business data (D-010).
 *
 * ============================================================================
 *  WHY preload() AND paint() ARE SEPARATE FUNCTIONS
 * ============================================================================
 * This mirrors PBFreightClient.fetchFreight() deliberately, on request: that
 * function does no DOM work and returns data; pb-builder.js owns a
 * `freightRequestToken` counter, increments it before calling, and checks it
 * in the `.then()` before writing state or touching the DOM. A stale reply
 * from a superseded request is silently discarded by the CALLER, not by the
 * data-fetching function.
 *
 * The same shape applies here for the same reason: a customer can toggle
 * colours faster than a preload resolves, and a slow load for a colour they
 * have since deselected must not paint over what they are looking at now.
 *
 *   preload(colorKeys)              -> Promise<Map>, no DOM writes, cacheable
 *   paint(container, colorKeys, loaded) -> synchronous, DOM only
 *
 * pb-builder.js owns the token, exactly as it does for freight:
 *
 *   var token = ++confettiRequestToken;
 *   PBConfettiRender.preload(state.colors).then(function (loaded) {
 *     if (token !== confettiRequestToken) return; // superseded — discard
 *     PBConfettiRender.paint(container, state.colors, loaded);
 *   });
 *
 * ============================================================================
 *  CACHING — fetch each colour once per session, never again
 * ============================================================================
 * A module-level Map keeps, per colour id, either an in-flight Promise or a
 * resolved list of successfully-loaded piece sources. Re-selecting a colour
 * already in the Map returns instantly — no new network request, because the
 * browser's own HTTP cache is never even consulted a second time for that URL.
 * Concurrent preload() calls for the same not-yet-cached colour share the same
 * in-flight Promise rather than issuing duplicate fetches.
 *
 * warmPalette() prefetches every catalog colour not yet in the cache, meant to
 * be called once after the first real render, scheduled by the CALLER during
 * idle time (requestIdleCallback). This module does not decide *when* to warm
 * the palette — only pb-builder.js decides that, matching the same
 * caller-owns-scheduling split as the token guard above.
 *
 * ============================================================================
 *  GRACEFUL DEGRADATION — a broken image must never break the preview
 * ============================================================================
 * If one piece image fails to load, that piece is dropped from the pool for
 * its colour rather than failing the whole render. If ALL FOUR pieces for a
 * selected colour fail, that colour is silently absent from the pile rather
 * than blocking rendering of the colours that did load. This is D-004's
 * "never block the customer" philosophy applied to a lower-stakes feature — a
 * slightly imperfect preview is fine; an error state on a decorative panel
 * is not.
 */

window.PBConfettiRender = (function () {
  'use strict';

  /**
   * Fixed CSS render size for a piece (unchanged from the legacy page):
   * background layer 62–92px, foreground layer 88–126px. NOT container-size
   * dependent, so this width covers every container this ever renders into,
   * including 2x-retina headroom (126 * 2 = 252 < 300).
   */
  var PIECE_WIDTH = 300;

  /** colorId -> Promise<string[]> | string[] (resolved sources, possibly empty) */
  var cache = {};

  function pieceUrl(base) {
    // Every stored URL already carries `?v=...` (D-034/D-046 convention), so
    // `&` is always the correct join — never `?`.
    return base + '&width=' + PIECE_WIDTH;
  }

  /**
   * Load one image, resolving null on failure instead of rejecting. A single
   * bad piece must degrade the pool for its colour, not blow up Promise.all.
   */
  function loadOne(url) {
    return new Promise(function (resolve) {
      var sized = pieceUrl(url);
      var img = new Image();
      /**
       * Resolve with `sized`, NOT the original `url`. The browser fetched
       * `sized` (the ?width=300 version); resolving with the un-widthed URL
       * would make paint() re-request the full 1500x1500 original for the
       * piece it actually renders, discarding everything this file exists to
       * save. Caught before shipping, not after — see the test that guards it.
       */
      img.onload = function () { resolve(sized); };
      img.onerror = function () { resolve(null); };
      img.src = sized;
    });
  }

  /**
   * Preload a colour's 4 pieces, or return the cached result/in-flight
   * promise if this colour has been requested before this session.
   */
  function loadColor(colorId) {
    if (cache[colorId]) return Promise.resolve(cache[colorId]);

    var cfg = window.PBConfig;
    var entry = cfg.color.catalog.filter(function (c) { return c.id === colorId; })[0];
    var urls = entry && entry.confettiPieces ? entry.confettiPieces : [];

    var promise = Promise.all(urls.map(loadOne)).then(function (results) {
      var loaded = results.filter(Boolean);
      cache[colorId] = loaded; // replace the in-flight promise with the resolved array
      return loaded;
    });

    cache[colorId] = promise;
    return promise;
  }

  /**
   * Resolve every requested colour's loaded piece sources.
   *
   * @param {string[]} colorKeys
   * @returns {Promise<Object<string,string[]>>} colorId -> array of loaded
   *          src strings (already carrying `?width=300`). A colour with no
   *          usable pieces (all 4 failed, or an unknown id) maps to [].
   */
  function preload(colorKeys) {
    var keys = (colorKeys || []).filter(Boolean);
    return Promise.all(keys.map(loadColor)).then(function (results) {
      var out = {};
      keys.forEach(function (key, i) { out[key] = results[i]; });
      return out;
    });
  }

  /** Prefetch every catalog colour not already cached. Fire-and-forget. */
  function warmPalette() {
    var cfg = window.PBConfig;
    if (!cfg) return;
    cfg.color.catalog.forEach(function (c) { loadColor(c.id); });
  }

  /* =====================================================================
   * PLACEMENT ALGORITHM — ported from sections/confetti-builder.liquid
   * =================================================================== */

  function rand(min, max) { return Math.random() * (max - min) + min; }
  function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }

  /**
   * Sample one point within a w×h box: 60% uniform, 40% mildly centre-biased
   * (double-random averaging), with ±12% jitter so pieces reach the edges
   * rather than leaving a border gap. Exported for direct testing — the
   * legacy page tuned this by eye, and these are the numbers that produced
   * results indistinguishable from it in review.
   */
  function sampleFullBoxPoint(w, h) {
    var uniform = Math.random() < 0.60;
    var u, v;
    if (uniform) {
      u = Math.random();
      v = Math.random();
    } else {
      u = (Math.random() + Math.random()) / 2;
      v = (Math.random() + Math.random()) / 2;
    }
    var x = u * w + rand(-0.12, 0.12) * w;
    var y = v * h + rand(-0.12, 0.12) * h;
    return { x: x, y: y };
  }

  /**
   * Total piece count for a container of the given pixel area. Unchanged from
   * the legacy formula (D-046 keeps it as-is): proportional to area, clamped
   * to a sane range regardless of how large or small the panel is.
   */
  function densityFor(area) {
    return Math.max(230, Math.min(375, Math.floor(area / 2200)));
  }

  /**
   * Build one <img> piece element. `loadedByColor` supplies ONLY the sources
   * that actually loaded for this colour — never the full static list — so a
   * failed piece can never be picked and rendered broken.
   */
  function buildPiece(colorId, loadedByColor, w, h, layer) {
    var pool = loadedByColor[colorId];
    if (!pool || !pool.length) return null;

    var point = sampleFullBoxPoint(w, h);
    var size = layer === 'bg' ? rand(62, 92) : rand(88, 126);
    var opacity = layer === 'bg' ? rand(0.78, 0.92) : rand(0.88, 0.98);
    var safePad = size * 0.05;

    var img = document.createElement('img');
    img.className = 'pb__confetti-piece';
    img.src = pick(pool);
    img.alt = '';
    img.loading = 'eager'; // already preloaded; lazy-loading would only add latency here
    img.style.left = clamp(point.x, safePad, w - safePad) + 'px';
    img.style.top = clamp(point.y, safePad, h - safePad) + 'px';
    img.style.setProperty('--r', rand(0, 360) + 'deg');
    img.style.setProperty('--s', size.toFixed(0) + 'px');
    img.style.setProperty('--o', opacity.toFixed(2));
    img.style.zIndex = String(
      layer === 'bg' ? 900 + Math.floor(rand(0, 120)) : 1100 + Math.floor(rand(0, 220))
    );
    return img;
  }

  /**
   * Paint a scattered pile into `container` for the given colours, using
   * ALREADY-LOADED sources from a prior preload() call. Synchronous and DOM-
   * only, per the caller-owns-the-token design above.
   *
   * Colours with no successfully-loaded pieces are silently skipped — see the
   * graceful-degradation note in the file header.
   *
   * @param {HTMLElement} container
   * @param {string[]} colorKeys
   * @param {Object<string,string[]>} loadedByColor  from preload()
   * @returns {{piecesRendered: number, colorsRendered: number}}
   */
  function paint(container, colorKeys, loadedByColor) {
    if (!container) return { piecesRendered: 0, colorsRendered: 0 };

    var keys = (colorKeys || []).filter(function (k) {
      return loadedByColor[k] && loadedByColor[k].length;
    });

    // Batch insertion: build the whole pile off-DOM, then swap it in with a
    // single reflow instead of one per appendChild — the legacy script did
    // the latter, which is fine for one page but not for a step that
    // repaints on every colour toggle.
    var fragment = document.createDocumentFragment();

    if (keys.length) {
      var rect = container.getBoundingClientRect();
      var w = rect.width;
      var h = rect.height;

      if (w > 0 && h > 0) {
        var totalTarget = densityFor(w * h);
        var k = keys.length;
        var bgTotal = Math.floor(totalTarget * 0.62);
        var fgTotal = totalTarget - bgTotal;
        var bgPer = Math.floor(bgTotal / k);
        var fgPer = Math.floor(fgTotal / k);
        var bgRemainder = bgTotal - bgPer * k;
        var fgRemainder = fgTotal - fgPer * k;
        var placed = 0;

        keys.forEach(function (key) {
          var count = bgPer + (bgRemainder > 0 ? 1 : 0);
          if (bgRemainder > 0) bgRemainder--;
          for (var i = 0; i < count; i++) {
            var el = buildPiece(key, loadedByColor, w, h, 'bg');
            if (el) { fragment.appendChild(el); placed++; }
          }
        });

        keys.forEach(function (key) {
          var count = fgPer + (fgRemainder > 0 ? 1 : 0);
          if (fgRemainder > 0) fgRemainder--;
          for (var i = 0; i < count; i++) {
            var el = buildPiece(key, loadedByColor, w, h, 'fg');
            if (el) { fragment.appendChild(el); placed++; }
          }
        });

        container.textContent = ''; // clear existing pile in the same pass
        container.appendChild(fragment);
        return { piecesRendered: placed, colorsRendered: keys.length };
      }
    }

    container.textContent = '';
    return { piecesRendered: 0, colorsRendered: 0 };
  }

  return {
    preload: preload,
    paint: paint,
    warmPalette: warmPalette,
    // Exported for direct unit testing, same pattern as pb-freight-fallback.js.
    sampleFullBoxPoint: sampleFullBoxPoint,
    densityFor: densityFor
  };
})();
